<template>
    <div class="container tab-panel p-3">
        <div class="row">
            <div class="col-6">
                <h5>Larm och kontroller</h5>
            </div>
            <div class="col-6 text-end">
                <span class="text-muted me-2" v-show="showReloadText">Laddar...</span>
                <button class="btn btn-primary me-2" @click="onReload()" title="Uppdatera listorna">
                    <svg style="width: 1em; height: 1em; vertical-align: -0.125em;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M463.5 224l8.5 0c13.3 0 24-10.7 24-24l0-128c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1c-87.5 87.5-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8l119.5 0z"/></svg>
                </button>
                <button class="btn btn-primary" @click="onEditControl(null)">Lägg till kontroll</button>
            </div>
        </div>
        <ul class="nav nav-tabs">
            <li class="nav-item">
                <button id="dashboard" :class="'btn btn-link nav-link' + (tab === 'alarms' ? ' active' : '')"
                    @click="tab = 'alarms'">Larm ({{ alarms.length }})</button>
            </li>
            <li class="nav-item">
                <button id="devices" :class="'btn btn-link nav-link' + (tab === 'checks' ? ' active' : '')"
                    @click="tab = 'checks'">Kontroller</button>
            </li>
        </ul>
        <div class="mb-3" v-if="tab === 'alarms'">
            <div class="text-end">
                <input class="mt-3 ms-3" type="checkbox" id="showClosed" v-model="showClosed" @change="reloadAlarms">
                <label for="showClosed">Visa även stängda larm</label>
            </div>
            <table class="table">
                <thead>
                    <tr>
                        <th>Utlöst</th>
                        <th>Beskrivning</th>
                        <th>Mätserie</th>
                        <th>Mätplats</th>
                        <th>Period / tid</th>
                        <th>Värde</th>
                        <th>Gräns</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="a in alarms" :key="a.id">
                        <td>{{ new Date(a.created).toLocaleString('sv') }}</td>
                        <td>{{ (a.is_closed ? '[Stängt] ' : '') + a.text }}</td>
                        <td>{{ getMeasuringSeriesText(a) }}</td>
                        <td>{{ a.apartment_number }}</td>
                        <td>{{ getPeriodDescription(a) }}</td>
                        <td><a href="#" @click="$emit('show-device', { apartmentId: a.apartment_id, deviceId: a.device_id })" title="Visa avläsningar">{{
                            seriesHelpers.formatFieldValue(a.data.field, a.data.value) + ' ' + seriesHelpers.getFieldUnit(a.data.field) }}</a>
                            <button class="btn btn-link text-body" @click="$emit('show-statistics', { apartment: a.apartment_id, mp: a.measuring_point_id, field: a.data.field })" title="Visa statistik"><i class="bi bi-bar-chart-fill"></i></button>
                        </td>
                        <td><a href="#" @click="onEditControl(a.alarm_checks_id)" title="Redigera kontroll">{{
                            seriesHelpers.formatFieldValue(a.data.field, a.data.limit) + ' ' + seriesHelpers.getFieldUnit(a.data.field) }}</a>
                        </td>
                        <td><button class="btn btn-sm btn-link text-danger" @click="onCloseAlarm(a.id, !a.is_closed)">{{ a.is_closed
                                ? 'Öppna' : 'Stäng'}}</button></td>
                    </tr>
                </tbody>
            </table>

        </div>
        <div class="mb-3" v-else-if="tab === 'checks'">
            <table class="table mt-3">
                <thead>
                    <tr>
                        <th>Kontroll</th>
                        <th>Mätserie</th>
                        <th>Larma om</th>
                        <th>Körs</th>
                        <th>Senast utförd</th>
                        <th>Nästa körning</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="c in checks" :key="c.id" :style="c.is_enabled ? '' : 'text-decoration: line-through; color: #999;'">
                        <td>{{ c.description }}</td>
                        <td>{{ c.measurement_type }}</td>
                        <td>{{ c.limits }}</td>
                        <td>{{ c.interval }}</td>
                        <td>{{ c.last_done }}</td>
                        <td>{{ c.next_check }}</td>
                        <td>
                            <button class="btn btn-sm btn-primary" @click="onEditControl(c.id)">Ändra</button>
                            &nbsp;&nbsp;
                            <button class="btn btn-sm btn-danger" @click="onDeleteControl(c.id)">Ta bort</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <AlarmCheckModal ref="alarmCheckComponent" id="alarmCheckModal" :data="modalData" :apartments="props.apartments"
            @close="alarmCheckModal.hide()" @reload="reloadChecks" />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { Modal } from 'bootstrap'
import alarmsService from '@/services/alarmsService'
import seriesHelpers from '@/helpers/seriesHelpers'
import AlarmCheckModal from '@/components/AlarmCheckModal.vue'

const props = defineProps({
    apartments: Array,
})
const emit = defineEmits(['show-device', 'show-statistics'])
const route = useRoute()
const alarmCheckModal = ref(null)
const alarmCheckComponent = ref(null)
const tab = ref('alarms')

const alarms = ref([])
const checks = ref([])
const showClosed = ref(false)
const showReloadText = ref(false)

const modalData = reactive({
    siteId: route.params.id,
    alarmCheckId: null,
    isEnabled: true,
    checkType: null,
    intervalType: null,
    intervalCount: null,
    delayType: null,
    delayCount: null,
    noDelay: false,
    settings: {
        mpType: null,
        field: null,
        sameAllMonths: 'true',
        lower: null,
        upper: null,
        months: {},
        allApartments: 'true',
        apartmentsSelected: {},
        limitType: null,
        limitValue: null,
        flags: null,
        flagsName: null,
        email: null,
        sendInstantly: false,
    },
    errors: {
        checkType: null,
        intervalType: null,
        intervalCount: null,
        delayType: null,
        delayCount: null,
        mpType: null,
        field: null,
        limitType: null,
        limitValue: null,
        limits: null,
        apartments: null,
        flags: null,
        flagsName: null,
        email: null,
    },
    form: {
        mpTypes: [],
        fields: [],
        mps: [],
        mpSelection: [],
    },
})

onMounted(() => {
    alarmCheckModal.value = new Modal(document.getElementById('alarmCheckModal'), { backdrop: 'static', keyboard: false })
})

onBeforeUnmount(() => {
    alarmCheckModal.value.hide()
})

async function onEditControl(id) {
    modalData.alarmCheckId = id
    let alarmCheck = null
    if (id == null) {
        modalData.isEnabled = true
        modalData.checkType = null
        modalData.intervalType = null
        modalData.intervalCount = null
        modalData.delayType = null
        modalData.delayCount = null
        modalData.noDelay = false
        modalData.settings = {
            mpType: null,
            field: null,
            sameAllMonths: 'true',
            lower: null,
            upper: null,
            allApartments: 'true',
            apartmentsSelected: {},
            months: {
                0: { lower: null, upper: null },
                1: { lower: null, upper: null },
                2: { lower: null, upper: null },
                3: { lower: null, upper: null },
                4: { lower: null, upper: null },
                5: { lower: null, upper: null },
                6: { lower: null, upper: null },
                7: { lower: null, upper: null },
                8: { lower: null, upper: null },
                9: { lower: null, upper: null },
                10: { lower: null, upper: null },
                11: { lower: null, upper: null },
            },
            limitType: null,
            limitValue: null,
            flags: null,
            flagsName: null,
            email: null,
            sendInstantly: false,
        }
    } else {
        alarmCheck = await alarmsService.getAlarmCheck(id)
        modalData.isEnabled = alarmCheck.is_enabled
        modalData.checkType = alarmCheck.check_type
        modalData.intervalType = alarmCheck.interval_type
        modalData.intervalCount = alarmCheck.interval_count
        modalData.delayType = alarmCheck.delay_type
        modalData.delayCount = alarmCheck.delay_count
        modalData.noDelay = !alarmCheck.delay_count
        modalData.settings.mpType = alarmCheck.settings.mpType
        modalData.settings.field = alarmCheck.settings.field
        modalData.settings.limitType = alarmCheck.settings.limitType
        modalData.settings.limitValue = alarmCheck.settings.limitValue
        modalData.settings.flags = alarmCheck.settings.flags
        modalData.settings.flagsName = alarmCheck.settings.flagsName
        modalData.settings.email = alarmCheck.settings.email
        modalData.settings.sendInstantly = alarmCheck.settings.sendInstantly
        if (alarmCheck.settings.months && Object.keys(alarmCheck.settings.months).length > 0) {
            modalData.settings.sameAllMonths = 'false'
            modalData.settings.months = alarmCheck.settings.months
            modalData.settings.lower = null
            modalData.settings.upper = null
        } else {
            modalData.settings.sameAllMonths = 'true'
            for (let i = 0; i < 12; i++) {
                modalData.settings.months[i] = { lower: null, upper: null }
            }
            modalData.settings.lower = alarmCheck.settings.lower
            modalData.settings.upper = alarmCheck.settings.upper
        }
        if (alarmCheck.settings.apartments && alarmCheck.settings.apartments.length > 0) {
            modalData.settings.allApartments = 'false'
            for (const a of alarmCheck.settings.apartments) {
                modalData.settings.apartmentsSelected[a] = 'true'
            }
        } else {
            modalData.settings.allApartments = 'true'
        }
    }
    modalData.errors = {
        checkType: null,
        intervalType: null,
        intervalCount: null,
        delayType: null,
        delayCount: null,
        mpType: null,
        field: null,
        limitType: null,
        limitValue: null,
        limits: null,
        flags: null,
        flagsName: null,
        email: null,
    }
    modalData.form.mpTypes = []
    modalData.form.fields = []
    modalData.form.mps = []
    const includedTypes = {}
    for (const a of props.apartments) {
        for (const d of a.devices) {
            modalData.form.mps.push({ id: d.measuringPointId, idNumber: d.idNumber, measurement: seriesHelpers.getMeasurementFromType(d.type), place: d.place })
            if (!includedTypes[d.type]) {
                includedTypes[d.type] = true
                modalData.form.mpTypes.push(seriesHelpers.getMeasurementFromType(d.type))
            }
        }
    }
    alarmCheckComponent.value.updateDeviceTypes()
    alarmCheckModal.value.show()
}

function onDeleteControl(id) {
    if (confirm('Är du säker på att du vill ta bort kontrollen?')) {
        alarmsService.deleteAlarmCheck(id)
        checks.value = checks.value.filter(c => c.id !== id)
    }
}

function onCloseAlarm(id, close) {
    alarmsService.closeAlarm(id, close)
    if (close && !showClosed.value) {
        alarms.value = alarms.value.filter(a => a.id !== id)
    } else {
        alarms.value.find(a => a.id === id).is_closed = close
    }
}

function getMeasuringSeriesText(a) {
    const measuremtneLabel = a.measuring_point_type ? seriesHelpers.getMeasurementLabelFromType(a.measuring_point_type) : 'Alla'
    const fieldLabel = seriesHelpers.getFieldLabel(a.data.field, { lowerCase: true }) ?? ''
    return `${measuremtneLabel} ${fieldLabel}`
}

function getPeriodDescription(a) {
    if (a.alarm_check_type === 'collection') {
        switch (a.data.interval) {
            case 'hour': return 'Per timme'
            case 'day': return 'Per dag'
            case 'week': return 'Per vecka'
            case 'month': return 'Per månad' 
            case 'year': return 'Per år'
            default: return 'Okänd period'
        }
    }
    if (a.alarm_check_type === 'status') {
        return localeDateAndTime(a.data.timestamp)
    }
    const startDate = a.data.start
    switch (a.data.interval) {
        case 'hour': return new Date(startDate).toLocaleDateString('sv') + ' ' + new Date(startDate).toLocaleTimeString('sv', { hour: '2-digit', minute: '2-digit' })
        case 'day': return new Date(startDate).toLocaleDateString('sv')
        case 'month': return new Date(startDate).toLocaleString('sv', { month: 'long' }) + ' ' + new Date(startDate).getFullYear()
        case 'year': return new Date(startDate).getFullYear()
    }
}

async function reloadAlarms() {
    const fetchedAlarms = await alarmsService.getAlarms(route.params.id, showClosed.value)

    alarms.value = []

    for (const a of fetchedAlarms) {
        switch (a.alarm_check_type) {
            case 'collection':
                if (a.data.operator === '>') {
                    a.text = `Fler än ${a.data.limit} enheter saknas. (${a.data.readingsCount}/${a.data.devicesCount})`
                } else {
                    a.text = `Färre än ${a.data.limit}% av enheterna kommer in. (${a.data.readingsCount}/${a.data.devicesCount})`
                }
                break
            case 'usage': a.text = `${a.data.operator === '<' ? 'Låg' : 'Hög'} förbrukning`; break
            case 'average': 
                a.text = 'Medelvärde'
                switch (a.data.interval) {
                    case 'hour': a.text += ' per timme'; break
                    case 'day': a.text += ' per dag'; break
                    case 'week': a.text += ' per vecka'; break
                    case 'month': a.text += ' per månad'; break
                    case 'year': a.text += ' per år'; break
                }
                break
            case 'value':
                a.text = `${new Date(a.data.timestamp).toLocaleString('sv')}: ${seriesHelpers.getFieldLabel(a.data.field)} ${a.data.value} ${seriesHelpers.getFieldUnit(a.data.field)} ${a.data.operator} ${a.data.limit} ${seriesHelpers.getFieldUnit(a.data.field)}`
                break
            case 'status':
                a.text = `${a.data.flagsName} (${a.data.field} ${a.data.flags})`
                break
        }
        alarms.value.push(a)
    }
}

async function reloadChecks() {
    const alarmChecks = await alarmsService.getAlarmChecks(route.params.id)

    checks.value = []

    for (const ac of alarmChecks) {
        let description = ''
        switch (ac.check_type) {
            case 'collection': description = 'Insamling'; break
            case 'usage': description = 'Förbrukning'; break
            case 'average': description = 'Medelvärde'; break
            case 'value': description = 'Värde'; break
            case 'status': description = 'Status'; break
        }

        let limits = ''
        if (ac.check_type === 'collection') {
            switch (ac.settings.limitType) {
                case 'count': limits = 'Fler än ' + ac.settings.limitValue + ' enheter saknas'; break
                case 'percent': limits = 'Mindre än ' + ac.settings.limitValue + '%'; break
            }
        } else if (ac.check_type === 'status') {
            limits = ac.settings.flagsName + ' (' + ac.settings.field + ' ' + ac.settings.flags + ')'
        } else {
            if (ac.settings.lower != null && ac.settings.upper != null) {
                limits += 'Utanför ' + ac.settings.lower + ' till ' + ac.settings.upper
            } else if (ac.settings.lower != null) {
                limits += 'Mindre än ' + ac.settings.lower
            } else if (ac.settings.upper != null) {
                limits += 'Större än ' + ac.settings.upper
            } else {
                limits += 'Olika per månad'
            }
        }

        let interval = '', last = false
        if (ac.check_type === 'collection') {
            if (ac.interval_count === 1) {
                interval = 'Varje '
            } else if (ac.interval_count === 2) {
                interval = 'Varannan '
            } else {
                interval = 'Var ' + ac.interval_count + ':e '
            }
            switch (ac.interval_type) {
                case 'hour': interval += 'timme'; break
                case 'day': interval += 'dag'; break
                case 'week': interval = 'vecka'; break
                case 'month': interval += 'månad'; break
                case 'year': interval = 'år'; break
            }
        } else if (ac.check_type === 'usage' || ac.check_type === 'average') {
            switch (ac.interval_type) {
                case 'hour': interval = 'Timme'; break
                case 'day': interval = 'Dag'; break
                case 'week': interval = 'Vecka'; break
                case 'month': interval = 'Månad'; break
                case 'year': interval = 'År'; break
            }
        } else {
            interval = 'Vid import'
            last = true
        }

        let mpType
        if (ac.settings.mpType === 'all') {
            mpType = 'Alla'
        } else if (ac.settings.mpType.includes('|')) {
            mpType = seriesHelpers.getMeasurementLabel(ac.settings.mpType.split('|')[1]) + ' ' + ac.settings.mpType.split('|')[0]
        } else {
            mpType = seriesHelpers.getMeasurementLabel(ac.settings.mpType)
        }
        if (ac.checkType === 'usage' || ac.checkType === 'average' || ac.checkType === 'value') {
            mpType += ' ' + seriesHelpers.getFieldLabel(ac.settings.field, { lowerCase: true })
        }

        checks.value.push({
            id: ac.id,
            description: description,
            measurement_type: mpType,
            limits,
            is_enabled: ac.is_enabled,
            interval: interval,
            last_done: ac.last_done ? localeDateAndTime(ac.last_done) : 'Aldrig',
            next_check: ac.next_check ? localeDateAndTime(ac.next_check) : 'Vid nästa import',
        })
    }
}

function localeDateAndTime(date) {
    return new Date(date).toLocaleDateString('sv') + ' ' + new Date(date).toLocaleTimeString('sv', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function onReload() {
    showReloadText.value = true
    reloadAlarms()
    reloadChecks()
    setTimeout(() => {
        showReloadText.value = false
    }, 500)
}

reloadAlarms()
reloadChecks()

</script>